import { recordedLabel } from "../cockpit/format";
import type {
  FacilityMatchView,
  FacilityPoint,
  FacilityReviewStatus,
  FacilitySegment,
  FacilityUploadCollision,
  FacilityUploadRow,
  FrsRefreshView,
  LocationVerdict,
} from "./types";

type ChipVariant = "default" | "ok" | "bad" | "info" | "blue" | "warn";

const SEGMENT_VARIANT: Record<FacilitySegment, ChipVariant> = {
  Producer: "blue",
  Compounder: "info",
  Converter: "default",
  Distributor: "info",
  Recycler: "default",
  Unclassified: "default",
};

export const VERIFIED_TARGET = 400;

export function overVerifiedTarget(count: number): boolean {
  return count > VERIFIED_TARGET;
}

export function segmentVariant(segment: FacilitySegment): ChipVariant {
  return SEGMENT_VARIANT[segment] ?? "default";
}

const REVIEW_VARIANT: Record<FacilityReviewStatus, ChipVariant> = {
  Verified: "ok",
  Rejected: "bad",
  Unclassified: "default",
};

const REVIEW_LABEL: Record<FacilityReviewStatus, string> = {
  Verified: "ResinSmart Verified",
  Rejected: "Rejected",
  Unclassified: "Unclassified",
};

export function reviewVariant(status: FacilityReviewStatus): ChipVariant {
  return REVIEW_VARIANT[status] ?? "default";
}

export function reviewLabel(status: FacilityReviewStatus): string {
  return REVIEW_LABEL[status] ?? status;
}

const UPLOAD_VARIANT: Record<FacilityUploadRow["action"], ChipVariant> = {
  Created: "ok",
  Matched: "blue",
  Invalid: "bad",
};

export function uploadVariant(action: FacilityUploadRow["action"]): ChipVariant {
  return UPLOAD_VARIANT[action] ?? "default";
}

export function collisionTitle(collision: FacilityUploadCollision): string {
  if (collision.sameAddress) return "Same address";
  if (collision.basis === "Address") return "Same street, nearby number";
  return "Same place";
}

export function collisionText(collision: FacilityUploadCollision): string {
  const where = [collision.address, collision.city, collision.stateCode].filter(Boolean).join(", ");
  const apart =
    collision.basis === "Location" && collision.distanceMiles !== null
      ? ` · ${collision.distanceMiles.toFixed(2)} mi away`
      : "";
  return `${collision.name}${where ? ` · ${where}` : ""}${apart} · names ${Math.round(collision.nameScore * 100)}% alike`;
}

const VERDICT_VARIANT: Record<LocationVerdict, ChipVariant> = {
  Confirmed: "ok",
  AddressOnly: "warn",
  NotListed: "default",
  NoLocation: "default",
  Failed: "bad",
};

const VERDICT_LABEL: Record<LocationVerdict, string> = {
  Confirmed: "Business confirmed",
  AddressOnly: "Address matches, point is off",
  NotListed: "Not listed on the map",
  NoLocation: "Nothing to check",
  Failed: "Check failed",
};

export function verdictVariant(verdict: LocationVerdict): ChipVariant {
  return VERDICT_VARIANT[verdict] ?? "default";
}

export function verdictLabel(verdict: LocationVerdict): string {
  return VERDICT_LABEL[verdict] ?? verdict;
}

export function reviewSummary(
  status: FacilityReviewStatus,
  reviewedByName: string | null,
  reviewedUtc: string | null,
): string | null {
  if (status === "Unclassified" || !reviewedByName) return null;
  const when = reviewedUtc ? recordedLabel(reviewedUtc) : "";
  return `${status} by ${reviewedByName}${when ? `, ${when}` : ""}`;
}

const METHOD_LABEL: Record<FacilityMatchView["method"], string> = {
  ParentOrgExact: "Parent company matches",
  ExactNormalized: "Name matches",
  Alias: "Known alias",
  Fuzzy: "Similar name",
  Manual: "Chosen by hand",
  None: "No candidate found",
};

export function methodLabel(method: FacilityMatchView["method"]): string {
  return METHOD_LABEL[method] ?? method;
}

export function confidenceLabel(score: number | null | undefined): string | null {
  if (score === null || score === undefined) return null;
  return `${Math.round(score * 100)}%`;
}

export function formatCoordinates(latitude: number | null, longitude: number | null): string {
  if (latitude === null || longitude === null) return "No coordinates";
  const ns = latitude >= 0 ? "N" : "S";
  const ew = longitude >= 0 ? "E" : "W";
  return `${Math.abs(latitude).toFixed(4)}° ${ns}, ${Math.abs(longitude).toFixed(4)}° ${ew}`;
}

export function formatLocation(city: string | null, stateCode: string | null): string {
  return [city, stateCode].filter(Boolean).join(", ") || "—";
}

export function placeOrNull(city: string | null, stateCode: string | null): string | null {
  return [city, stateCode].filter(Boolean).join(", ") || null;
}

export function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
  if (bytes >= 1024 ** 2) return `${Math.round(bytes / 1024 ** 2)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

export function percent(part: number, whole: number): string {
  if (whole === 0) return "0%";
  return `${((part / whole) * 100).toFixed(1)}%`;
}

export function refreshHeadline(refresh: FrsRefreshView): string {
  if (refresh.status === "Skipped") return "Skipped: EPA's file had not changed";
  if (refresh.status === "Running") return "Running";
  if (refresh.status === "Failed") return "Failed";
  const changed = refresh.rowsInserted + refresh.rowsUpdated;
  return `${changed.toLocaleString()} changed, ${refresh.rowsUnchanged.toLocaleString()} unchanged, ${refresh.rowsRetired.toLocaleString()} retired`;
}

export function countBySegment(points: readonly FacilityPoint[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const point of points) counts[point.segment] = (counts[point.segment] ?? 0) + 1;
  return counts;
}

export function epaReportUrl(registryId: string): string {
  return `https://ofmpub.epa.gov/frs_public2/fii_query_detail.disp_program_facility?p_registry_id=${encodeURIComponent(registryId)}`;
}

export function updatedLabel(at: Date): string {
  return `updated ${at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
}
